/**
 * Boss Unique Equipment
 *
 * Equipment that can ONLY drop from specific boss chests.
 * These items are more powerful than regular equipment of the same rarity
 * and have unique effects or stat combinations.
 *
 * Naming Convention: {bossTheme}{ItemType}
 * Each boss has 3 unique items: weapon, armor piece, and accessory
 */

// ============================================================
// TIER 1 BOSS UNIQUES (Level 10)
// ============================================================

export const TIER1_BOSS_UNIQUES = {
  // -----------------------------------------------------------
  // OGRE CHIEFTAIN UNIQUES
  // -----------------------------------------------------------
  ogresMaul: {
    id: 'ogresMaul',
    name: "Ogre's Maul",
    icon: '🔨',
    slot: 'weapon',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_ogre',
    description: 'A massive club wielded by the Ogre Chieftain. Crushes all in its path.',
    stats: {
      attack: 35,
      critDamage: 0.25,
    },
    setBonus: null,
  },
  chieftainsBelt: {
    id: 'chieftainsBelt',
    name: "Chieftain's Belt",
    icon: '🎗️',
    slot: 'belt',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_ogre',
    description: 'A thick hide belt worn by the Ogre Chieftain.',
    stats: {
      health: 80,
      defense: 12,
    },
  },
  thickHideVest: {
    id: 'thickHideVest',
    name: 'Thick Hide Vest',
    icon: '🦺',
    slot: 'top',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_ogre',
    description: 'Armor made from layered ogre hide. Incredibly tough.',
    stats: {
      defense: 25,
      health: 60,
    },
  },

  // -----------------------------------------------------------
  // GOBLIN KING UNIQUES
  // -----------------------------------------------------------
  crownOfGreed: {
    id: 'crownOfGreed',
    name: 'Crown of Greed',
    icon: '👑',
    slot: 'helmet',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_goblinKing',
    description: 'The Goblin King\'s crown, enchanted with gold-finding magic.',
    stats: {
      goldBonus: 0.15,
      luck: 8,
      defense: 10,
    },
  },
  goblinKingsScepter: {
    id: 'goblinKingsScepter',
    name: "Goblin King's Scepter",
    icon: '🏆',
    slot: 'weapon',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_goblinKing',
    description: 'A scepter that commands loyalty. And fear.',
    stats: {
      attack: 28,
      critChance: 0.08,
      goldBonus: 0.10,
    },
  },
  thievesCloak: {
    id: 'thievesCloak',
    name: "Thieves' Cloak",
    icon: '🧥',
    slot: 'cape',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_goblinKing',
    description: 'A cloak favored by the Goblin King\'s elite thieves.',
    stats: {
      evasion: 0.08,
      speed: 2,
      goldBonus: 0.05,
    },
  },

  // -----------------------------------------------------------
  // BANDIT LORD UNIQUES
  // -----------------------------------------------------------
  bladeOfBetrayal: {
    id: 'bladeOfBetrayal',
    name: 'Blade of Betrayal',
    icon: '🗡️',
    slot: 'weapon',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_banditLord',
    description: 'A sword that has tasted the blood of many allies-turned-enemies.',
    stats: {
      attack: 32,
      critChance: 0.12,
      critDamage: 0.15,
    },
  },
  banditLordsMask: {
    id: 'banditLordsMask',
    name: "Bandit Lord's Mask",
    icon: '🎭',
    slot: 'helmet',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_banditLord',
    description: 'A mask that strikes fear into merchants everywhere.',
    stats: {
      attack: 8,
      critChance: 0.05,
      evasion: 0.05,
    },
  },
  shadowstepBoots: {
    id: 'shadowstepBoots',
    name: 'Shadowstep Boots',
    icon: '👢',
    slot: 'boots',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_banditLord',
    description: 'Boots that make no sound. Perfect for ambushes.',
    stats: {
      speed: 4,
      evasion: 0.06,
      critChance: 0.04,
    },
  },

  // -----------------------------------------------------------
  // SPECTRAL OVERSEER UNIQUES
  // -----------------------------------------------------------
  phantomPickaxe: {
    id: 'phantomPickaxe',
    name: 'Phantom Pickaxe',
    icon: '⛏️',
    slot: 'weapon',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_mineOverseer',
    description: 'A ghostly pickaxe that phases through armor.',
    stats: {
      attack: 30,
      armorPenetration: 0.15,
    },
  },
  spectersLantern: {
    id: 'spectersLantern',
    name: "Specter's Lantern",
    icon: '🏮',
    slot: 'offhand',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_mineOverseer',
    description: 'A lantern that reveals hidden treasures and enemies.',
    stats: {
      defense: 8,
      lootQuality: 0.10,
      accuracy: 0.05,
    },
  },
  etherealChains: {
    id: 'etherealChains',
    name: 'Ethereal Chains',
    icon: '⛓️',
    slot: 'belt',
    rarity: 'rare',
    levelReq: 8,
    isBossUnique: true,
    bossId: 'boss_mineOverseer',
    description: 'Chains that bind the wearer to the spirit realm.',
    stats: {
      defense: 15,
      health: 50,
      evasion: 0.04,
    },
  },
};

// ============================================================
// TIER 2 BOSS UNIQUES (Level 25)
// ============================================================

export const TIER2_BOSS_UNIQUES = {
  // -----------------------------------------------------------
  // LICH UNIQUES
  // -----------------------------------------------------------
  staffOfUndeath: {
    id: 'staffOfUndeath',
    name: 'Staff of Undeath',
    icon: '🦯',
    slot: 'weapon',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_lich',
    description: 'A staff that channels the power of death itself.',
    stats: {
      attack: 55,
      critDamage: 0.30,
      health: -50, // Life steal theme - costs health
    },
  },
  lichsPhylactery: {
    id: 'lichsPhylactery',
    name: "Lich's Phylactery",
    icon: '💎',
    slot: 'pendant',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_lich',
    description: 'The vessel containing the Lich\'s soul fragment.',
    stats: {
      health: 120,
      defense: 20,
      healthRegen: 2,
    },
  },
  robesOfTheDamned: {
    id: 'robesOfTheDamned',
    name: 'Robes of the Damned',
    icon: '👘',
    slot: 'top',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_lich',
    description: 'Dark robes woven from the souls of the damned.',
    stats: {
      defense: 35,
      attack: 15,
      critDamage: 0.20,
    },
  },

  // -----------------------------------------------------------
  // FROST LICH UNIQUES
  // -----------------------------------------------------------
  frostbiteScepter: {
    id: 'frostbiteScepter',
    name: 'Frostbite Scepter',
    icon: '🧊',
    slot: 'weapon',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_frostLich',
    description: 'A scepter of eternal cold that freezes enemies solid.',
    stats: {
      attack: 52,
      critChance: 0.10,
      speed: -1, // Slows wielder too
    },
  },
  cloakOfEternalWinter: {
    id: 'cloakOfEternalWinter',
    name: 'Cloak of Eternal Winter',
    icon: '❄️',
    slot: 'cape',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_frostLich',
    description: 'A cloak that radiates freezing cold.',
    stats: {
      defense: 25,
      evasion: 0.08,
      health: 80,
    },
  },
  icecrownCirclet: {
    id: 'icecrownCirclet',
    name: 'Icecrown Circlet',
    icon: '💠',
    slot: 'helmet',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_frostLich',
    description: 'A circlet of pure ice that never melts.',
    stats: {
      defense: 22,
      critChance: 0.08,
      critDamage: 0.15,
    },
  },

  // -----------------------------------------------------------
  // PLAGUE RAT UNIQUES
  // -----------------------------------------------------------
  virulentFang: {
    id: 'virulentFang',
    name: 'Virulent Fang',
    icon: '🦷',
    slot: 'weapon',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_plagueRat',
    description: 'A dagger coated in deadly plague toxins.',
    stats: {
      attack: 48,
      critChance: 0.15,
      poisonDamage: 10, // DOT damage
    },
  },
  plaguebearersMask: {
    id: 'plaguebearersMask',
    name: "Plaguebearer's Mask",
    icon: '😷',
    slot: 'helmet',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_plagueRat',
    description: 'A mask that protects from all diseases.',
    stats: {
      defense: 20,
      health: 100,
      poisonResist: 0.50,
    },
  },
  infectedClaws: {
    id: 'infectedClaws',
    name: 'Infected Claws',
    icon: '🧤',
    slot: 'gloves',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_plagueRat',
    description: 'Clawed gloves dripping with infectious ichor.',
    stats: {
      attack: 18,
      critChance: 0.10,
      poisonDamage: 5,
    },
  },

  // -----------------------------------------------------------
  // ORC WARCHIEF UNIQUES
  // -----------------------------------------------------------
  orcishWarblade: {
    id: 'orcishWarblade',
    name: 'Orcish Warblade',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_orcWarchief',
    description: 'A massive blade forged for conquering armies.',
    stats: {
      attack: 62,
      critDamage: 0.35,
      defense: -5, // Offense over defense
    },
  },
  warchiefsBanner: {
    id: 'warchiefsBanner',
    name: "Warchief's Banner",
    icon: '🚩',
    slot: 'cape',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_orcWarchief',
    description: 'A war banner that inspires allies and intimidates foes.',
    stats: {
      attack: 20,
      health: 60,
      defense: 15,
    },
  },
  skullCrusherHelm: {
    id: 'skullCrusherHelm',
    name: 'Skull Crusher Helm',
    icon: '💀',
    slot: 'helmet',
    rarity: 'epic',
    levelReq: 22,
    isBossUnique: true,
    bossId: 'boss_orcWarchief',
    description: 'A helm decorated with the skulls of defeated enemies.',
    stats: {
      defense: 28,
      attack: 12,
      critDamage: 0.15,
    },
  },
};

// ============================================================
// TIER 3 BOSS UNIQUES (Level 45)
// ============================================================

export const TIER3_BOSS_UNIQUES = {
  // -----------------------------------------------------------
  // DRAGON UNIQUES
  // -----------------------------------------------------------
  dragonslayersEdge: {
    id: 'dragonslayersEdge',
    name: "Dragonslayer's Edge",
    icon: '🗡️',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_dragon',
    description: 'A blade forged from dragon teeth and scales.',
    stats: {
      attack: 95,
      critChance: 0.12,
      critDamage: 0.40,
    },
  },
  dragonscaleArmor: {
    id: 'dragonscaleArmor',
    name: 'Dragonscale Armor',
    icon: '🐲',
    slot: 'top',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_dragon',
    description: 'Armor crafted from impenetrable dragon scales.',
    stats: {
      defense: 65,
      health: 150,
      fireResist: 0.30,
    },
  },
  wyrmfireAmulet: {
    id: 'wyrmfireAmulet',
    name: 'Wyrmfire Amulet',
    icon: '🔥',
    slot: 'pendant',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_dragon',
    description: 'An amulet containing eternal dragonfire.',
    stats: {
      attack: 30,
      critDamage: 0.25,
      fireDamage: 20,
    },
  },

  // -----------------------------------------------------------
  // ELDER WYRM UNIQUES
  // -----------------------------------------------------------
  primordialFang: {
    id: 'primordialFang',
    name: 'Primordial Fang',
    icon: '🦷',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_elderWyrm',
    description: 'A fang from the oldest dragon in existence.',
    stats: {
      attack: 100,
      critChance: 0.15,
      armorPenetration: 0.20,
    },
  },
  ancientWyrmscale: {
    id: 'ancientWyrmscale',
    name: 'Ancient Wyrmscale',
    icon: '🛡️',
    slot: 'offhand',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_elderWyrm',
    description: 'A shield made from a single elder wyrm scale.',
    stats: {
      defense: 55,
      health: 200,
      allResist: 0.15,
    },
  },
  eldersCrown: {
    id: 'eldersCrown',
    name: "Elder's Crown",
    icon: '👑',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_elderWyrm',
    description: 'A crown worn by ancient dragon riders.',
    stats: {
      defense: 40,
      attack: 25,
      xpBonus: 0.10,
    },
  },

  // -----------------------------------------------------------
  // DEMON PRINCE UNIQUES
  // -----------------------------------------------------------
  bladeOfTheDamned: {
    id: 'bladeOfTheDamned',
    name: 'Blade of the Damned',
    icon: '🔱',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_demonPrince',
    description: 'A blade forged in the fires of hell.',
    stats: {
      attack: 105,
      critDamage: 0.50,
      health: -100, // Demonic price
    },
  },
  demonPrincesHorns: {
    id: 'demonPrincesHorns',
    name: "Demon Prince's Horns",
    icon: '😈',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_demonPrince',
    description: 'Horns that channel infernal power.',
    stats: {
      attack: 35,
      critChance: 0.12,
      fireDamage: 15,
    },
  },
  infernalWings: {
    id: 'infernalWings',
    name: 'Infernal Wings',
    icon: '🦇',
    slot: 'cape',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_demonPrince',
    description: 'Wings torn from the Demon Prince himself.',
    stats: {
      evasion: 0.12,
      speed: 5,
      attack: 20,
    },
  },

  // -----------------------------------------------------------
  // GRAND WITCH UNIQUES
  // -----------------------------------------------------------
  hexmastersStaff: {
    id: 'hexmastersStaff',
    name: "Hexmaster's Staff",
    icon: '🪄',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_grandWitch',
    description: 'A staff that amplifies all magical curses.',
    stats: {
      attack: 88,
      critChance: 0.18,
      magicDamage: 25,
    },
  },
  covensCrown: {
    id: 'covensCrown',
    name: "Coven's Crown",
    icon: '🎭',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_grandWitch',
    description: 'The crown of the witch coven leader.',
    stats: {
      defense: 35,
      attack: 30,
      critDamage: 0.20,
    },
  },
  robesOfTheGrandWitch: {
    id: 'robesOfTheGrandWitch',
    name: 'Robes of the Grand Witch',
    icon: '👗',
    slot: 'top',
    rarity: 'legendary',
    levelReq: 42,
    isBossUnique: true,
    bossId: 'boss_grandWitch',
    description: 'Enchanted robes of incredible power.',
    stats: {
      defense: 45,
      health: 120,
      magicDamage: 20,
    },
  },
};

// ============================================================
// TIER 4 BOSS UNIQUES (Level 65)
// ============================================================

export const TIER4_BOSS_UNIQUES = {
  // -----------------------------------------------------------
  // ABYSSAL LORD UNIQUES
  // -----------------------------------------------------------
  tridentOfTheAbyss: {
    id: 'tridentOfTheAbyss',
    name: 'Trident of the Abyss',
    icon: '🔱',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_abyssalLord',
    description: 'A trident that commands the deep ocean.',
    stats: {
      attack: 140,
      critChance: 0.15,
      critDamage: 0.45,
    },
  },
  abyssalLordsCrown: {
    id: 'abyssalLordsCrown',
    name: "Abyssal Lord's Crown",
    icon: '🌊',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_abyssalLord',
    description: 'A crown forged in the deepest trenches.',
    stats: {
      defense: 55,
      health: 200,
      waterResist: 0.40,
    },
  },
  depthsEmbrace: {
    id: 'depthsEmbrace',
    name: "Depth's Embrace",
    icon: '🐙',
    slot: 'top',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_abyssalLord',
    description: 'Armor that moves like living water.',
    stats: {
      defense: 70,
      evasion: 0.10,
      health: 150,
    },
  },

  // -----------------------------------------------------------
  // INFERNO LORD UNIQUES
  // -----------------------------------------------------------
  infernoLordsHeart: {
    id: 'infernoLordsHeart',
    name: "Inferno Lord's Heart",
    icon: '❤️‍🔥',
    slot: 'pendant',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_infernoLord',
    description: 'The still-burning heart of the Inferno Lord.',
    stats: {
      attack: 50,
      critDamage: 0.40,
      fireDamage: 35,
    },
  },
  flameImperatorsCrown: {
    id: 'flameImperatorsCrown',
    name: "Flame Imperator's Crown",
    icon: '🔥',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_infernoLord',
    description: 'A crown of eternal flames.',
    stats: {
      defense: 50,
      attack: 40,
      fireResist: 0.50,
    },
  },
  magmaforgedGreaves: {
    id: 'magmaforgedGreaves',
    name: 'Magmaforged Greaves',
    icon: '🦵',
    slot: 'boots',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_infernoLord',
    description: 'Boots forged in active volcanoes.',
    stats: {
      defense: 45,
      speed: 4,
      fireDamage: 15,
    },
  },

  // -----------------------------------------------------------
  // NIGHTMARE KING UNIQUES
  // -----------------------------------------------------------
  nightmaresEdge: {
    id: 'nightmaresEdge',
    name: "Nightmare's Edge",
    icon: '🌙',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_nightmareKing',
    description: 'A blade that cuts through dreams and reality.',
    stats: {
      attack: 135,
      critChance: 0.18,
      critDamage: 0.50,
    },
  },
  crownOfTerrors: {
    id: 'crownOfTerrors',
    name: 'Crown of Terrors',
    icon: '😱',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_nightmareKing',
    description: 'A crown that manifests the wearer\'s fears.',
    stats: {
      defense: 48,
      attack: 35,
      fearAura: 0.15, // Enemy debuff
    },
  },
  dreamwalkersCape: {
    id: 'dreamwalkersCape',
    name: "Dreamwalker's Cape",
    icon: '🌌',
    slot: 'cape',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_nightmareKing',
    description: 'A cape woven from dream essence.',
    stats: {
      evasion: 0.15,
      speed: 5,
      health: 100,
    },
  },

  // -----------------------------------------------------------
  // MAD ARCHIVIST UNIQUES
  // -----------------------------------------------------------
  tomeOfMadness: {
    id: 'tomeOfMadness',
    name: 'Tome of Madness',
    icon: '📖',
    slot: 'offhand',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_madArchivist',
    description: 'A book containing knowledge that shatters minds.',
    stats: {
      attack: 45,
      critChance: 0.12,
      magicDamage: 40,
    },
  },
  archivistsGlasses: {
    id: 'archivistsGlasses',
    name: "Archivist's Glasses",
    icon: '🤓',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_madArchivist',
    description: 'Glasses that reveal hidden truths.',
    stats: {
      accuracy: 0.20,
      critChance: 0.15,
      xpBonus: 0.15,
    },
  },
  robesOfForbiddenKnowledge: {
    id: 'robesOfForbiddenKnowledge',
    name: 'Robes of Forbidden Knowledge',
    icon: '📚',
    slot: 'top',
    rarity: 'legendary',
    levelReq: 62,
    isBossUnique: true,
    bossId: 'boss_madArchivist',
    description: 'Robes inscribed with forbidden texts.',
    stats: {
      defense: 55,
      health: 180,
      magicDamage: 30,
    },
  },
};

// ============================================================
// TIER 5 BOSS UNIQUES (Level 85)
// ============================================================

export const TIER5_BOSS_UNIQUES = {
  // -----------------------------------------------------------
  // VOID GOD UNIQUES
  // -----------------------------------------------------------
  voidGodsEssence: {
    id: 'voidGodsEssence',
    name: "Void God's Essence",
    icon: '🌀',
    slot: 'pendant',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_voidGod',
    description: 'A fragment of the Void God\'s power.',
    stats: {
      attack: 80,
      critDamage: 0.60,
      voidDamage: 50,
    },
  },
  crownOfTheVoid: {
    id: 'crownOfTheVoid',
    name: 'Crown of the Void',
    icon: '👑',
    slot: 'helmet',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_voidGod',
    description: 'A crown that grants dominion over nothingness.',
    stats: {
      defense: 75,
      health: 300,
      allStats: 0.08,
    },
  },
  cosmicDestroyer: {
    id: 'cosmicDestroyer',
    name: 'Cosmic Destroyer',
    icon: '💫',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_voidGod',
    description: 'A weapon capable of unmakingworlds.',
    stats: {
      attack: 200,
      critChance: 0.20,
      critDamage: 0.75,
    },
  },

  // -----------------------------------------------------------
  // FALLEN SERAPH UNIQUES
  // -----------------------------------------------------------
  corruptedHalo: {
    id: 'corruptedHalo',
    name: 'Corrupted Halo',
    icon: '😇',
    slot: 'helmet',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_fallenSeraph',
    description: 'A divine halo twisted by corruption.',
    stats: {
      defense: 70,
      attack: 50,
      holyDamage: 30,
      darkDamage: 30,
    },
  },
  fallenWings: {
    id: 'fallenWings',
    name: 'Fallen Wings',
    icon: '🪽',
    slot: 'cape',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_fallenSeraph',
    description: 'Wings stained by both light and darkness.',
    stats: {
      evasion: 0.18,
      speed: 8,
      allResist: 0.20,
    },
  },
  divineJudgement: {
    id: 'divineJudgement',
    name: 'Divine Judgement',
    icon: '⚖️',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_fallenSeraph',
    description: 'A sword that delivers ultimate justice.',
    stats: {
      attack: 190,
      critChance: 0.22,
      critDamage: 0.65,
    },
  },

  // -----------------------------------------------------------
  // REALITY BREAKER UNIQUES
  // -----------------------------------------------------------
  shardOfBrokenReality: {
    id: 'shardOfBrokenReality',
    name: 'Shard of Broken Reality',
    icon: '💎',
    slot: 'offhand',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_realityBreaker',
    description: 'A fragment of shattered existence.',
    stats: {
      defense: 60,
      attack: 60,
      dimensionalDamage: 50,
    },
  },
  paradoxHelm: {
    id: 'paradoxHelm',
    name: 'Paradox Helm',
    icon: '🔮',
    slot: 'helmet',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_realityBreaker',
    description: 'A helm that exists in multiple timelines.',
    stats: {
      defense: 65,
      evasion: 0.15,
      timeWarp: 0.10, // Extra turn chance
    },
  },
  dimensionRipper: {
    id: 'dimensionRipper',
    name: 'Dimension Ripper',
    icon: '🌀',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_realityBreaker',
    description: 'A blade that cuts through dimensions.',
    stats: {
      attack: 195,
      armorPenetration: 0.30,
      critDamage: 0.70,
    },
  },

  // -----------------------------------------------------------
  // ENTROPY SOVEREIGN UNIQUES
  // -----------------------------------------------------------
  entropySovereignsCrown: {
    id: 'entropySovereignsCrown',
    name: "Entropy Sovereign's Crown",
    icon: '💀',
    slot: 'helmet',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_entropySovereign',
    description: 'The crown of ultimate decay.',
    stats: {
      defense: 80,
      health: 350,
      entropyAura: 0.20, // Enemy stat reduction
    },
  },
  decayBringer: {
    id: 'decayBringer',
    name: 'Decay Bringer',
    icon: '⚰️',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_entropySovereign',
    description: 'A weapon that accelerates entropy.',
    stats: {
      attack: 210,
      critChance: 0.25,
      decayDamage: 60,
    },
  },
  robesOfEndingTime: {
    id: 'robesOfEndingTime',
    name: 'Robes of Ending Time',
    icon: '⏳',
    slot: 'top',
    rarity: 'mythic',
    levelReq: 82,
    isBossUnique: true,
    bossId: 'boss_entropySovereign',
    description: 'Robes woven from the fabric of dying universes.',
    stats: {
      defense: 90,
      health: 400,
      allStats: 0.10,
    },
  },
};

// ============================================================
// COMBINED EXPORTS
// ============================================================

export const ALL_BOSS_UNIQUES = {
  ...TIER1_BOSS_UNIQUES,
  ...TIER2_BOSS_UNIQUES,
  ...TIER3_BOSS_UNIQUES,
  ...TIER4_BOSS_UNIQUES,
  ...TIER5_BOSS_UNIQUES,
};

/**
 * Get boss unique equipment by ID
 */
export function getBossUnique(itemId) {
  return ALL_BOSS_UNIQUES[itemId] || null;
}

/**
 * Get all boss uniques for a specific boss
 */
export function getBossUniquesForBoss(bossId) {
  return Object.values(ALL_BOSS_UNIQUES).filter(item => item.bossId === bossId);
}

/**
 * Check if an item is a boss unique
 */
export function isBossUnique(itemId) {
  return itemId in ALL_BOSS_UNIQUES;
}

export default ALL_BOSS_UNIQUES;
